/**
 * Taking a lobby seat: shared by the lobby ("Take this seat" / invitee "Join
 * game") and the join-by-code screen.
 *
 *   1. Private game reached with a code: redeem it first (POST
 *      /:id/redeem-code; idempotent for an existing pending invite).
 *   2. POST /:id/join { slot?, color? } with the viewer's default colour.
 *   3. If that colour was taken in the meantime, join again without a colour
 *      (the server then picks the viewer's default if free, else the lowest).
 *
 * The API calls are injected so the sequence is unit-testable.
 */

import type { GameResponse } from '@warcarp/api-types'
import { HttpError } from './api'

export interface TakeSeatRequest {
  gameId: string
  slot?: number
  color?: number
  /** Invite code to redeem first (private games reached by code). */
  code?: string
  isPublic: boolean
}

export interface TakeSeatApi {
  redeem: (gameId: string, code: string) => Promise<unknown>
  join: (body: { gameId: string; slot?: number; color?: number }) => Promise<GameResponse>
}

/** The server's machine code: `code` when sent, else the `error` field (e.g. 'SLOT_TAKEN'). */
function errorCode(err: HttpError): string | undefined {
  return err.code ?? (typeof err.body?.error === 'string' ? err.body.error : undefined)
}

export async function takeLobbySeat(req: TakeSeatRequest, api: TakeSeatApi): Promise<GameResponse> {
  if (req.code && !req.isPublic) await api.redeem(req.gameId, req.code)
  const body = { gameId: req.gameId, ...(req.slot != null ? { slot: req.slot } : {}) }
  try {
    return await api.join(req.color != null ? { ...body, color: req.color } : body)
  } catch (err) {
    if (req.color != null && err instanceof HttpError && err.status === 409 && errorCode(err) === 'COLOR_TAKEN') {
      return api.join(body)
    }
    throw err
  }
}

/** User-facing message for a failed seat take. */
export function takeSeatErrorMessage(err: unknown): string {
  if (err instanceof HttpError) {
    if (err.status === 409 && errorCode(err) === 'SLOT_TAKEN') return 'That seat was just taken. Pick another.'
    if (err.status === 401) return 'That code is not valid for this game.'
    return err.message || 'Could not join the game'
  }
  return err instanceof Error ? err.message : 'Could not join the game'
}
